import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { branchesAPI, showtimesAPI, moviesAPI } from '../api';
import { MapPin, Phone, Mail, Building, Calendar, X } from 'lucide-react';
import { Card } from '../components/ui/card';
import { useSettings } from '../contexts/SettingsContext';
import { usePageTitle } from '../hooks/usePageTitle';

const Branches = () => {
  const { settings } = useSettings();
  usePageTitle('Salonlarımız');
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedBranchId, setExpandedBranchId] = useState(null);
  const [movies, setMovies] = useState([]);
  const [loadingMovies, setLoadingMovies] = useState(false);

  useEffect(() => {
    loadBranches();
  }, []);

  const loadBranches = async () => {
    try {
      const response = await branchesAPI.getAll();
      setBranches(response.data);
    } catch (error) {
      console.error('Error loading branches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBranchClick = async (branch) => {
    // Eğer aynı branch'e tıklanırsa kapat, farklı branch'e tıklanırsa aç
    if (expandedBranchId === branch._id) {
      setExpandedBranchId(null);
      setMovies([]);
      return;
    }

    setExpandedBranchId(branch._id);
    setLoadingMovies(true);
    setMovies([]);

    try {
      // Bugünün tarihini YYYY-MM-DD formatında al
      const today = new Date().toISOString().split('T')[0];
      
      // O şubedeki bugünkü seansları al
      const showtimesResponse = await showtimesAPI.getAll({
        branch_id: branch._id,
        show_date: today
      });
      
      const showtimes = showtimesResponse.data;

      if (showtimes.length === 0) {
        setMovies([]);
        setLoadingMovies(false);
        return;
      }

      // Unique movie ID'leri al
      const movieIds = [...new Set(showtimes.map(st => st.movie_id))];
      
      // Her film için bilgi ve seansları grupla
      const moviesData = await Promise.all(
        movieIds.map(async (movieId) => {
          try {
            const movieResponse = await moviesAPI.getById(movieId);
            const movie = movieResponse.data;
            
            // Arşivlenmiş filmleri atla
            if (movie.status === 'archived') {
              return null;
            }
            
            // Bu filme ait seansları filtrele ve format + dil ile grupla
            const movieShowtimes = showtimes.filter(st => st.movie_id === movieId);
            
            // Format ve dil bazlı gruplama
            const showtimesByFormatAndLanguage = movieShowtimes.reduce((acc, st) => {
              const format = st.format || 'Standart';
              const language = st.language || 'Türkçe Dublaj';
              const key = `${format} - ${language}`;
              
              if (!acc[key]) {
                acc[key] = [];
              }
              acc[key].push(st.show_time);
              return acc;
            }, {});

            return {
              ...movie,
              showtimesByFormatAndLanguage
            };
          } catch (error) {
            console.error(`Error loading movie ${movieId}:`, error);
            return null;
          }
        })
      );

      // Null olmayan ve arşivlenmemiş filmleri filtrele
      const validMovies = moviesData.filter(m => m !== null);
      setMovies(validMovies);
    } catch (error) {
      console.error('Error loading showtimes:', error);
    } finally {
      setLoadingMovies(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Helmet>
        <title>Salonlarımız - {settings.site_name || 'Cinefora'}</title>
        <meta name="description" content="Cinefora sinema salonlarımız. Türkiye genelindeki sinema lokasyonlarımız, iletişim bilgileri ve adresler." />
        <meta name="keywords" content="salonlar, sinema lokasyonları, adres, telefon, cinefora" />
      </Helmet>
      <Header />
      <div className="container mx-auto px-6 py-32">
        <h1 className="text-4xl font-bold text-white mb-8">Salonlarımız</h1>
        
        {loading ? (
          <p className="text-white text-center">Yükleniyor...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {branches.map((branch) => (
              <React.Fragment key={branch._id}>
                {/* Branch Card */}
                <Card 
                  className={`bg-gray-900 border-gray-800 p-6 cursor-pointer hover:border-[#FDB714] transition-colors ${
                    expandedBranchId === branch._id ? 'border-[#FDB714]' : ''
                  }`}
                  onClick={() => handleBranchClick(branch)}
                >
                  <h3 className="text-white font-bold text-xl mb-4 hover:text-[#FDB714] transition-colors">
                    {branch.name}
                    {expandedBranchId === branch._id && (
                      <span className="ml-2 text-[#FDB714]">▼</span>
                    )}
                  </h3>
                  <div className="space-y-3 text-gray-300">
                    <div className="flex items-start gap-2">
                      <MapPin size={18} className="text-[#FDB714] mt-1 flex-shrink-0" />
                      <span>{branch.address}</span>
                    </div>
                    {branch.phone && (
                      <div className="flex items-center gap-2">
                        <Phone size={18} className="text-[#FDB714]" />
                        <span>{branch.phone}</span>
                      </div>
                    )}
                    {branch.email && (
                      <div className="flex items-center gap-2">
                        <Mail size={18} className="text-[#FDB714]" />
                        <span className="text-sm break-all">{branch.email}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Building size={18} className="text-[#FDB714]" />
                      <span>{branch.halls_count} Salon</span>
                    </div>
                  </div>
                </Card>

                {/* Expandable Section - Bugünkü Filmler */}
                {expandedBranchId === branch._id && (
                  <div className="col-span-1 md:col-span-2 lg:col-span-3 bg-gray-800 border border-gray-700 rounded-lg p-6 animate-in slide-in-from-top duration-300">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold text-[#FDB714]">
                        {branch.name} - Bugün Oynayan Filmler
                      </h2>
                      <button
                        onClick={() => setExpandedBranchId(null)}
                        className="text-gray-400 hover:text-white transition-colors"
                      >
                        <X size={24} />
                      </button>
                    </div>

                    {loadingMovies ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FDB714]"></div>
                      </div>
                    ) : movies.length === 0 ? (
                      <div className="text-center py-12">
                        <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-400 text-lg">Bugün bu şubede gösterim bulunmamaktadır.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {movies.map((movie) => (
                          <div key={movie._id} className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                            <div className="flex gap-4">
                              {/* Movie Poster */}
                              <Link 
                                to={`/film/${movie.slug || movie._id}`}
                                className="flex-shrink-0"
                              >
                                <img
                                  src={movie.poster || 'https://via.placeholder.com/200x300?text=No+Image'}
                                  alt={movie.title}
                                  className="w-24 h-36 object-cover rounded-lg hover:opacity-80 transition-opacity"
                                  onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/200x300?text=No+Image';
                                  }}
                                />
                              </Link>

                              {/* Movie Info */}
                              <div className="flex-grow">
                                <Link 
                                  to={`/film/${movie.slug || movie._id}`}
                                >
                                  <h3 className="text-xl font-bold text-white mb-2 hover:text-[#FDB714] transition-colors cursor-pointer">
                                    {movie.title}
                                  </h3>
                                </Link>
                                
                                {/* Formats, Languages and Showtimes */}
                                <div className="space-y-3">
                                  {Object.entries(movie.showtimesByFormatAndLanguage).map(([formatAndLang, times]) => (
                                    <div key={formatAndLang}>
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="text-[#FDB714] font-semibold text-sm">
                                          {formatAndLang}
                                        </span>
                                      </div>
                                      <div className="flex flex-wrap gap-2">
                                        {times.sort().map((time, index) => (
                                          <span
                                            key={index}
                                            className="bg-gray-700 text-white px-3 py-1 rounded text-sm font-medium hover:bg-[#FDB714] hover:text-black transition-colors cursor-pointer"
                                          >
                                            {time}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Branches;
